import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

const enableNewRelic = true;

interface NewRelicConfig {
  licenseKey: string;
  applicationID: string;
  accountID: string;
  trustKey: string;
  agentID: string;
  proxy?: {
    beacon: string;
  };
  errorBeacon: string;
  spa?: {
    enabled: boolean;
    autoStart: boolean;
  };
  distributedTracing?: {
    enabled: boolean;
  };
  customAttributes?: {
    enabled: boolean;
  };
  webVitals?: {
    enabled: boolean;
  };
  userAttributes?: {
    tenantId: string;
    identityServiceId: string;
  };
  privacy?: {
    cookies_enabled: boolean;
  };
  ajax?: {
    deny_list: string[];
  };
  softNavigations?: {
    enabled: boolean;
    autoStart: boolean;
  };
}

// Default configuration
const defaultConfig: NewRelicConfig = {
  licenseKey: 'NRJS-3018cce14ac2c3bb27e',
  applicationID: '601561630',
  accountID: '2997544',
  trustKey: '2997544',
  agentID: '601561630',
  proxy: {
    beacon: 'bam.nr-data.net',
  },
  errorBeacon: 'bam.nr-data.net',
  spa: {
    autoStart: true,
    enabled: true,
  },
  distributedTracing: {
    enabled: true,
  },
  customAttributes: {
    enabled: true,
  },
  webVitals: {
    enabled: true,
  },
  privacy: {
    cookies_enabled: true,
  },
  ajax: {
    deny_list: ['bam.nr-data.net'],
  },
  softNavigations: {
    enabled: true,
    autoStart: true,
  },
};

// Global configuration
let currentConfig: NewRelicConfig = { ...defaultConfig };

// Get NewRelic instance
const getNewRelic = (): BrowserAgent | null => {
  const newrelic = window.newrelic || null;
  if (newrelic !== null && 'start' in newrelic) {
    return newrelic;
  }
  return null;
};

// Initialize NewRelic with configuration
const initializeNewRelic = (
  config?: Partial<NewRelicConfig>,
): BrowserAgent | null => {
  if (config) {
    currentConfig = { ...currentConfig, ...config };
  }

  if (!enableNewRelic) {
    return null;
  }

  const agent = new BrowserAgent({
    init: {
      spa: {
        enabled: currentConfig?.spa?.enabled,
        autoStart: currentConfig?.spa?.autoStart,
      },
      distributed_tracing: {
        enabled: currentConfig?.distributedTracing?.enabled,
      },
      soft_navigations: {
        enabled: currentConfig?.softNavigations?.enabled,
        autoStart: currentConfig?.softNavigations?.autoStart,
      },
      privacy: {
        cookies_enabled: currentConfig?.privacy?.cookies_enabled,
      },
      ajax: {
        deny_list: currentConfig?.ajax?.deny_list,
      },
    },
    info: {
      beacon: currentConfig?.proxy?.beacon,
      errorBeacon: currentConfig?.errorBeacon,
      licenseKey: currentConfig.licenseKey,
      applicationID: currentConfig.applicationID,
      userAttributes: JSON.stringify(currentConfig.userAttributes),
      sa: 1,
    },
    loader_config: {
      accountID: currentConfig.accountID,
      trustKey: currentConfig.trustKey,
      agentID: currentConfig.agentID,
      licenseKey: currentConfig.licenseKey,
      applicationID: currentConfig.applicationID,
    },
  });

  return agent;
};

// Update configuration and reinitialize
const updateConfig = (newConfig: Partial<NewRelicConfig>): void => {
  if (!enableNewRelic) {
    return;
  }
  currentConfig = { ...currentConfig, ...newConfig };
  reinitialize();
};

// Reinitialize NewRelic with current configuration
const reinitialize = (): void => {
  if (!enableNewRelic) {
    return;
  }
  // Clear existing instance
  if (typeof window !== 'undefined') {
    (window as any).newrelic = null;
  }
  // Initialize with updated config
  initializeNewRelic();
};

// Send page view
const sendPageView = (name: string, host?: string): void => {
  if (!enableNewRelic) {
    return;
  }
  const newrelic = getNewRelic();
  if (!newrelic) {
    return;
  }
  newrelic.setPageViewName(name, host);
};

// Add custom event
const addCustomEvent = (
  name: string,
  attributes?: Record<string, unknown>,
): void => {
  if (!enableNewRelic) {
    return;
  }
  const newrelic = getNewRelic();
  if (!newrelic) {
    return;
  }
  newrelic.recordCustomEvent(name, attributes);
};

// Send page action
const sendPageAction = (
  name: string,
  attributes?: Record<string, unknown>,
): void => {
  if (!enableNewRelic) {
    return;
  }
  const newrelic = getNewRelic();
  if (!newrelic) {
    return;
  }
  newrelic.addPageAction(name, attributes);
};

// Send error
const sendError = (error: Error, extraInfo?: React.ErrorInfo): void => {
  if (!enableNewRelic) {
    return;
  }
  const newrelic = getNewRelic();
  if (!newrelic) {
    return;
  }
  try {
    newrelic.noticeError(error, { extra: extraInfo });
  } catch (e) {
    // Silently handle error sending errors
  }
};

// Get current configuration
const getConfig = (): NewRelicConfig => {
  return { ...currentConfig };
};

// Check if NewRelic is initialized
const isInitialized = (): boolean => {
  const newrelic = getNewRelic();
  return newrelic !== null;
};

// Export the functional API
export const newRelicUtils = {
  initialize: initializeNewRelic,
  updateConfig,
  reinitialize,
  sendPageView,
  addCustomEvent,
  sendPageAction,
  sendError,
  getConfig,
  isInitialized,
  getNewRelic,
};

export default newRelicUtils;
